import type { ClientDuplicateGroup, ClientListItem, DuplicateMatchReason } from "@planerius/contracts/cabinet"
import type { ReactNode } from "react"
import { useEffect, useState } from "react"

import { useCabinetAccess } from "../../../access/use-access"
import { fetchClients } from "../../../api/clients.api"
import { fetchDuplicates, markClientsDistinct, mergePhoneMatches } from "../../../api/duplicates.api"
import { isApiProblem } from "../../../api/problem"
import { JournalDialog } from "../../../journal/appointment-dialogs"
import { useIsPhone } from "../../../journal/use-viewport"
import { ProblemScreen } from "../../../shell/states"
import { resolveBranch } from "../../../shell/use-branch"
import { Badge, Button, Chip, Chips, Skeleton, TextField } from "../../../ui"
import { useResource } from "../use-resource"
import styles from "./duplicates.module.css"
import { REASON_LABEL, REASON_TONE, pairSub, pairsWord } from "./merge-model"

const REASONS: readonly DuplicateMatchReason[] = ["phone", "name_birthdate", "name_created_close"]

/**
 * 3-А7 «Дубли» — /clients/duplicates. Самый частый сценарий первой недели после переезда:
 * база пришла из Excel, один телефон записан трижды, плюс клиент сам завёл карточку в боте.
 *
 * У КАЖДОЙ ПАРЫ — «ЭТО РАЗНЫЕ ЛЮДИ». Мама и ребёнок на одном номере — норма; без этого
 * действия список предложений станет вечным шумом, и в него перестанут заходить.
 *
 * МАССОВОЕ ДЕЙСТВИЕ — ТОЛЬКО ДЛЯ ТОЧНЫХ СОВПАДЕНИЙ ТЕЛЕФОНА, с числом в подтверждении
 * и не на телефоне (ТЗ). Без него 400 предложений после импорта не разобрать никогда.
 */
export function DuplicatesScreen(props: {
  readonly onBack: () => void
  readonly onOpenClient: (clientId: string) => void
  readonly onCompare: (leftId: string, rightId: string) => void
}): ReactNode {
  const { context } = useCabinetAccess()
  const timeZone = resolveBranch(context, null)?.timezone ?? context.tenant.defaultTimezone
  const isPhone = useIsPhone()

  const [reason, setReason] = useState<DuplicateMatchReason>("phone")
  const [chosen, setChosen] = useState(false)
  const [tick, setTick] = useState(0)
  const [more, setMore] = useState<{ readonly items: readonly ClientDuplicateGroup[]; readonly cursor: string | null }>({
    items: [],
    cursor: null,
  })
  const [loadingMore, setLoadingMore] = useState(false)
  const [distinct, setDistinct] = useState<ClientDuplicateGroup | null>(null)
  const [massOpen, setMassOpen] = useState(false)
  const [manualOpen, setManualOpen] = useState(false)
  const [outcome, setOutcome] = useState<string | null>(null)

  const list = useResource((signal) => fetchDuplicates({ reason, cursor: null }, signal), `dups:${reason}:${tick}`)
  const counts = list.data?.counts ?? null
  const total = counts === null ? null : counts.phone + counts.name_birthdate + counts.name_created_close

  // Первый показ — самый сильный непустой признак: пустой чип «телефон» при 260 совпадениях
  // по имени читался бы как «база чистая».
  useEffect(() => {
    if (chosen || counts === null || counts[reason] > 0) return
    const first = REASONS.find((item) => counts[item] > 0)
    if (first !== undefined && first !== reason) setReason(first)
  }, [chosen, counts, reason])

  useEffect(() => {
    setMore({ items: [], cursor: list.data?.nextCursor ?? null })
  }, [list.data])

  const reload = (): void => {
    setTick((value) => value + 1)
  }

  if (list.problem !== null && list.data === null) {
    return <ProblemScreen problem={list.problem} onRetry={list.reload} />
  }

  const items = [...(list.data?.items ?? []), ...more.items]

  const loadMore = async (): Promise<void> => {
    if (more.cursor === null) return
    setLoadingMore(true)
    try {
      const page = await fetchDuplicates({ reason, cursor: more.cursor })
      setMore((previous) => ({ items: [...previous.items, ...page.items], cursor: page.nextCursor }))
    } finally {
      setLoadingMore(false)
    }
  }

  return (
    <div className={styles.screen}>
      <div className={styles.crumbs}>
        <button type="button" className={styles.crumbLink} onClick={props.onBack}>
          Клиенты
        </button>
        <span className={styles.crumbSep} aria-hidden="true">
          ›
        </span>
        <span className={styles.crumbTitle}>Дубли</span>
        {total !== null && total > 0 ? <Badge tone="warn">{total}</Badge> : null}
        <div className={styles.headActions}>
          <Button size="sm" onClick={() => setManualOpen(true)}>
            Объединить вручную
          </Button>
          {!isPhone && counts !== null && counts.phone > 0 ? (
            <Button size="sm" variant="primary" onClick={() => setMassOpen(true)}>
              Объединить все точные совпадения по телефону · {counts.phone}
            </Button>
          ) : null}
        </div>
      </div>

      {outcome === null ? null : (
        <div className={styles.outcome} role="status">
          {outcome}
        </div>
      )}

      {list.data === null ? (
        <div className={styles.center}>
          <Skeleton width={240} height={6} radius={3} />
          <div className={styles.centerTitle}>Ищем совпадения</div>
          <div className={styles.muted}>Можно продолжать работать — мы покажем результат, когда закончим.</div>
        </div>
      ) : total === 0 ? (
        <div className={styles.center}>
          <div className={styles.centerTitle}>Совпадений не нашли. База чистая</div>
          <div className={styles.muted}>
            Если вы знаете про дубль, которого система не видит (сменил номер и фамилию), — объедините вручную.
          </div>
          <Button size="sm" onClick={() => setManualOpen(true)}>
            Объединить вручную
          </Button>
        </div>
      ) : (
        <>
          <div className={styles.toolbar}>
            <Chips>
              {REASONS.map((item) => (
                <Chip
                  key={item}
                  active={reason === item}
                  onClick={() => {
                    setChosen(true)
                    setReason(item)
                  }}
                >
                  {REASON_LABEL[item]} · {counts?.[item] ?? 0}
                </Chip>
              ))}
            </Chips>
            <span className={styles.sortNote}>Сортировка: сила совпадения</span>
          </div>
          <div className={styles.list}>
            {items.length === 0 ? (
              <div className={styles.muted}>По этому признаку совпадений нет.</div>
            ) : (
              items.map((group) => {
                const [a, b] = group.clients
                if (a === undefined || b === undefined) return null
                return (
                  <div key={`${a.id}:${b.id}`} className={styles.pair}>
                    {[a, b].map((card) => (
                      <div key={card.id} className={styles.pairCell}>
                        <button type="button" className={styles.pairName} onClick={() => props.onOpenClient(card.id)}>
                          {card.displayName}
                        </button>
                        <div className={styles.pairSub}>{pairSub(card, timeZone)}</div>
                      </div>
                    ))}
                    <div className={styles.pairActions}>
                      <Badge tone={REASON_TONE[group.reason]}>{REASON_LABEL[group.reason]}</Badge>
                      <div className={styles.pairButtons}>
                        <Button size="sm" variant="primary" onClick={() => props.onCompare(a.id, b.id)}>
                          Сравнить
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setDistinct(group)}>
                          Это разные люди
                        </Button>
                      </div>
                    </div>
                  </div>
                )
              })
            )}
            {more.cursor === null ? null : (
              <Button className={styles.more} size="sm" loading={loadingMore} onClick={() => void loadMore()}>
                Показать ещё
              </Button>
            )}
          </div>
        </>
      )}

      {distinct === null ? null : (
        <DistinctDialog
          group={distinct}
          onClose={() => setDistinct(null)}
          onDone={() => {
            setDistinct(null)
            setOutcome("Пара убрана из предложений навсегда — система больше не предложит их объединить.")
            reload()
          }}
        />
      )}
      {massOpen && counts !== null ? (
        <MassDialog
          pairs={counts.phone}
          onClose={() => setMassOpen(false)}
          onDone={(merged, skipped) => {
            setMassOpen(false)
            setOutcome(
              skipped === 0
                ? `Объединено: ${pairsWord(merged)}. Каждое слияние можно отменить из карточки клиента.`
                : `Объединено: ${pairsWord(merged)}. Ещё ${skipped} — тройные дубли: их карточка уже участвовала в другой паре, они остались в списке и сливаются следующим шагом.`,
            )
            reload()
          }}
          onStale={() => {
            setMassOpen(false)
            setOutcome(null)
            reload()
          }}
        />
      ) : null}
      {manualOpen ? (
        <ManualDialog
          onClose={() => setManualOpen(false)}
          onCompare={(left, right) => {
            setManualOpen(false)
            props.onCompare(left, right)
          }}
        />
      ) : null}
    </div>
  )
}

function DistinctDialog(props: {
  readonly group: ClientDuplicateGroup
  readonly onClose: () => void
  readonly onDone: () => void
}): ReactNode {
  const [note, setNote] = useState("")
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const [a, b] = props.group.clients

  const submit = async (): Promise<void> => {
    if (a === undefined || b === undefined) return
    setBusy(true)
    setProblem(null)
    try {
      await markClientsDistinct([a.id, b.id], note.trim() === "" ? undefined : note.trim())
      props.onDone()
    } catch (error) {
      if (!isApiProblem(error)) throw error
      setProblem(error.detail ?? error.title)
    } finally {
      setBusy(false)
    }
  }

  return (
    <JournalDialog title="Это разные люди" onClose={props.onClose}>
      <div className={styles.dialogBody}>
        <p className={styles.muted}>
          «{a?.displayName}» и «{b?.displayName}» больше не будут предлагаться к объединению — навсегда. Так бывает
          с мамой и ребёнком на одном номере.
        </p>
        <TextField
          label="Почему разные"
          hint="Необязательно: «мать и дочь, один номер»"
          value={note}
          maxLength={300}
          onChange={(event) => setNote(event.target.value)}
        />
        {problem === null ? null : <div className={styles.problem}>{problem}</div>}
        <div className={styles.dialogButtons}>
          <Button onClick={props.onClose}>Отмена</Button>
          <Button variant="primary" loading={busy} onClick={() => void submit()}>
            Больше не предлагать
          </Button>
        </div>
      </div>
    </JournalDialog>
  )
}

function MassDialog(props: {
  readonly pairs: number
  readonly onClose: () => void
  readonly onDone: (merged: number, skipped: number) => void
  readonly onStale: () => void
}): ReactNode {
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const submit = async (): Promise<void> => {
    setBusy(true)
    setProblem(null)
    try {
      const result = await mergePhoneMatches(props.pairs)
      props.onDone(result.merged, result.skipped)
    } catch (error) {
      if (!isApiProblem(error)) throw error
      if (error.status === 409) {
        setProblem(`${error.detail ?? error.title}`)
        return
      }
      setProblem(error.detail ?? error.title)
    } finally {
      setBusy(false)
    }
  }

  return (
    <JournalDialog title={`Объединить ${pairsWord(props.pairs)} с одинаковым телефоном?`} onClose={props.onClose}>
      <div className={styles.dialogBody}>
        <p className={styles.muted}>
          В каждой паре главной станет карточка с большим числом визитов (при равенстве — более старая): её имя, дата
          рождения и основной телефон. Телефоны, заметки, метки и визиты второй карточки переедут целиком, счётчики
          пересчитаются, рекламное согласие — по «меньше прав».
        </p>
        <p className={styles.muted}>
          <b>Если ошиблись — любое слияние можно отменить</b> из карточки клиента: вторая карточка не удаляется, а
          помечается слитой. Тройные дубли сливаются по шагам — оставшаяся пара появится в списке.
        </p>
        {problem === null ? null : (
          <div className={styles.problem}>
            {problem}
            <button type="button" className={styles.inlineLink} onClick={props.onStale}>
              Обновить список
            </button>
          </div>
        )}
        <div className={styles.dialogButtons}>
          <Button onClick={props.onClose}>Отмена</Button>
          <Button variant="primary" loading={busy} disabled={problem !== null} onClick={() => void submit()}>
            Объединить {pairsWord(props.pairs)}
          </Button>
        </div>
      </div>
    </JournalDialog>
  )
}

/** «Объединить вручную»: администратор знает про дубль, которого система не видит. */
function ManualDialog(props: {
  readonly onClose: () => void
  readonly onCompare: (leftId: string, rightId: string) => void
}): ReactNode {
  const [first, setFirst] = useState<ClientListItem | null>(null)
  const [second, setSecond] = useState<ClientListItem | null>(null)

  return (
    <JournalDialog title="Объединить вручную" onClose={props.onClose}>
      <div className={styles.dialogBody}>
        <p className={styles.muted}>Найдите две карточки одного человека — дальше сравним их поле за полем.</p>
        <ClientPicker label="Первая карточка" picked={first} exclude={second?.id ?? null} onPick={setFirst} />
        <ClientPicker label="Вторая карточка" picked={second} exclude={first?.id ?? null} onPick={setSecond} />
        <div className={styles.dialogButtons}>
          <Button onClick={props.onClose}>Отмена</Button>
          <Button
            variant="primary"
            deniedReason={first === null || second === null ? "Выберите обе карточки" : undefined}
            onClick={() => {
              if (first !== null && second !== null) props.onCompare(first.id, second.id)
            }}
          >
            Сравнить
          </Button>
        </div>
      </div>
    </JournalDialog>
  )
}

function ClientPicker(props: {
  readonly label: string
  readonly picked: ClientListItem | null
  readonly exclude: string | null
  readonly onPick: (client: ClientListItem | null) => void
}): ReactNode {
  const [query, setQuery] = useState("")
  const [found, setFound] = useState<readonly ClientListItem[]>([])

  useEffect(() => {
    const search = query.trim()
    if (search.length < 2) {
      setFound([])
      return
    }
    const controller = new AbortController()
    const timer = setTimeout(() => {
      void fetchClients({ search, limit: 8 }, controller.signal)
        .then((result) => setFound(result.items))
        .catch(() => setFound([]))
    }, 300)
    return () => {
      clearTimeout(timer)
      controller.abort()
    }
  }, [query])

  return (
    <div>
      <TextField
        label={props.label}
        hint="Имя или последние цифры телефона"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value)
          props.onPick(null)
        }}
      />
      <div className={styles.found}>
        {found
          .filter((item) => item.id !== props.exclude)
          .map((item) => (
            <button
              key={item.id}
              type="button"
              className={styles.foundItem}
              aria-pressed={props.picked?.id === item.id}
              onClick={() => props.onPick(item)}
            >
              <span>{item.displayName}</span>
              <span className={styles.pairSub}>
                {item.phoneMasked ?? "без телефона"} · {item.visitsCount}
              </span>
            </button>
          ))}
      </div>
    </div>
  )
}
